import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Progress, Text, useTheme } from '@Sage/hoi-poi-ui';
import classNames from 'classnames';
import { getNumberAsLocaleNumber } from 'utils/numbers';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';

import './styles.scss';

const RecipientsForm = memo(
    ({
        selectedRecipients = 0,
        maxRecipients = 0,
        remainingEmails = 0,
        onAddRecipients,
        onCancel,
        onSave,
    }) => {
        const theme = useTheme();

        const safeSelectedRecipients = useMemo(
            () => Math.max(Number(selectedRecipients) || 0, 0),
            [selectedRecipients],
        );
        const safeMaxRecipients = useMemo(
            () => Math.max(Number(maxRecipients) || 0, 0),
            [maxRecipients],
        );
        const safeRemainingEmails = useMemo(
            () => Math.max(Number(remainingEmails) || 0, 0),
            [remainingEmails],
        );

        const progressMax = safeMaxRecipients || 1;
        const progressValue = Math.min(safeSelectedRecipients, progressMax);

        const remainingEmailsLabel = getLiteralWithParameters('label_emails_remaining', [
            getNumberAsLocaleNumber(safeRemainingEmails),
        ]);
        const hasRecipients = safeSelectedRecipients > 0;

        return (
            <div className="recipients-form">
                <div className="recipients-form__summary">
                    <div className="recipients-form__summary-left">
                        <div
                            className={classNames('recipients-form__summary-icon', {
                                'recipients-form__summary-icon--active': hasRecipients,
                            })}
                        >
                            <Icon
                                name="multipleUsers"
                                size="large"
                                color={
                                    hasRecipients
                                        ? theme.colors.actionMajor[500]
                                        : theme.colors.grey[500]
                                }
                            />
                        </div>
                        <div className="recipients-form__summary-copy">
                            <Text type="h6">{getNumberAsLocaleNumber(safeSelectedRecipients)}</Text>
                            <Text type="body" color={theme.colors.textLight.secondary}>
                                {getLiteral('label_recipients_selected')}
                            </Text>
                        </div>
                    </div>
                    <Button type="primary" icon="personAdd" onClick={onAddRecipients}>
                        {getLiteral('action_add_recipients')}
                    </Button>
                </div>

                <div className="recipients-form__usage">
                    <div className="recipients-form__usage-header">
                        <Text type="h5">{getLiteral('label_marketing_campaigns_limit_usage')}</Text>
                        <Text>{remainingEmailsLabel}</Text>
                    </div>
                    <Progress
                        max={progressMax}
                        value={progressValue}
                        className="recipients-form__progress-bar"
                    />
                    <div className="recipients-form__usage-caption">
                        <Icon name="infoOutlined" size="smallMedium" />
                        <Text type="badges" color={theme.colors.textLight.secondary}>
                            {getLiteral('label_marketing_campaigns_send_emails_within_limits')}
                        </Text>
                    </div>
                </div>

                <div className="recipients-form__actions">
                    <Button type="secondary" size="small" onClick={onCancel}>
                        {getLiteral('action_cancel')}
                    </Button>
                    <Button type="primary" size="small" onClick={onSave}>
                        {getLiteral('action_save')}
                    </Button>
                </div>
            </div>
        );
    },
);

RecipientsForm.displayName = 'RecipientsForm';

RecipientsForm.propTypes = {
    selectedRecipients: PropTypes.number,
    maxRecipients: PropTypes.number,
    remainingEmails: PropTypes.number,
    onAddRecipients: PropTypes.func,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
};

export default RecipientsForm;
